import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { NavLink, Stack } from '@eneco/sparky';

import type { SideNavContent } from './side-nav-content.types';

/**
 * `SideNav` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy component (`libs/sitecore/components/content/src/SideNav/SideNav.tsx`): a vertical
 * list of links, the current page marked active. Each item's link is a flat, `adapter.toLink`-ready
 * object after the Sitecore lane's multilist unwrap — never `item.href`/`item.text` directly (see
 * `side-nav-content.types.ts`).
 *
 * See `side-nav-content.types.ts` — no Sanity content source for the list exists yet, so this renders
 * an empty `<nav>` on `?_cms=sanity` today.
 */
const SideNav: FC = () => {
  const { adapter } = useCms();
  const content = useContent<SideNavContent>();
  const items = content.items ?? [];

  return (
    // TODO: Add a good aria-label to indicate the type of nav
    <nav>
      <Stack gap="2" as="ul">
        {items.map((item, index) => {
          const link = adapter.toLink(item.link);
          if (!link?.href) return null;
          return (
            <li key={item.id ?? String(index)}>
              <NavLink variant="secondary" isCurrent={item.active} href={link.href}>
                {link.text || item.displayName}
              </NavLink>
            </li>
          );
        })}
      </Stack>
    </nav>
  );
};

export default SideNav;
